import { loanOptionsConstants } from 'config'

/**
 * Exclude loans from a portfolio run by their own servicing attributes, rather than by tag.
 *
 * Tags are applied by hand and drift. Longhorn's statement rule excludes four kinds of loan and
 * their tags covered two of them, so a run that correctly skipped 104 tagged loans still
 * statemented 11 loans whose `Unsecured Loan` / `Line of Credit` tags had never been applied. The
 * interest type below is read off the loan itself, so it cannot drift that way - and it sits beside
 * the tag picker rather than replacing it, because the other three exclusions have no usable
 * attribute on the loan: unsecured and new construction have no field at all, and
 * `creditFacilityType` exists but is unpopulated, so a facility picker here was either a no-op or
 * excluded the whole book. Those stay tag-only until the field is actually populated.
 *
 * Options come from the platform's own list, so nothing here is specific to one servicer.
 *
 * ⚠️ `loanOptionsConstants.interestType` holds TWO non-Dutch variants, `Non Dutch` and
 * `Non Dutch Excluding IR`, and the backend matches on the exact value. A servicer excluding
 * "non-Dutch" has to select both; selecting only `Non Dutch` silently leaves the other kind
 * receiving statements. Hence the hint below naming the trap rather than a generic one.
 */
export function LoanAttributeExclusions({
  interestTypes,
  onChangeInterestTypes,
  className = '',
}: {
  interestTypes: string[]
  onChangeInterestTypes: (values: string[]) => void
  className?: string
}) {
  const toggle = (value: string) =>
    onChangeInterestTypes(
      interestTypes.includes(value) ? interestTypes.filter((v) => v !== value) : [...interestTypes, value],
    )

  return (
    <div className={className}>
      <div className="flex flex-wrap items-center gap-x-2 text-sm text-gray-600">
        <span>
          Exclude by Interest Type <span className="text-gray-400">(optional: skips loans of these types)</span>
        </span>
        {interestTypes.length > 0 && (
          <span className="ml-auto flex items-center gap-2 text-xs text-gray-500">
            {interestTypes.length} selected
            <button type="button" className="text-shade-blue hover:underline" onClick={() => onChangeInterestTypes([])}>
              Clear
            </button>
          </span>
        )}
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 rounded border border-gray-200 p-1.5">
        {loanOptionsConstants.interestType.map((value) => {
          const on = interestTypes.includes(value)
          return (
            <button
              key={value}
              type="button"
              onClick={() => toggle(value)}
              className={`h-fit rounded-full border px-2 py-0.5 text-xs ${
                on
                  ? 'border-red-500 bg-red-500 text-white'
                  : 'border-gray-300 bg-white text-gray-600 hover:border-red-500'
              }`}
            >
              {value}
            </button>
          )
        })}
      </div>
      <p className="mt-1 text-xs text-gray-400">
        Read off each loan&rsquo;s servicing record, so unlike a tag it cannot be missed on a new loan. To exclude
        every non-Dutch loan, select both non-Dutch types.
      </p>
    </div>
  )
}
